import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Search, AlertCircle } from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';
import { CATEGORIA_COLOR, CATEGORIA_LABEL } from '../utils/constants';
import ConfirmDialog from '../ConfirmDialog';

/**
 * Seccion "Dependencias" del sidebar del modal de tarea.
 *
 * Renderiza dos subsecciones direccionales:
 *   - "Bloqueada por"  → tarea.bloqueada_por[]
 *   - "Bloquea a"      → tarea.bloquea_a[]
 *
 * Same-project only en 1.5: el picker de "agregar" solo lista tareas del mismo
 * proyecto, excluyendo la tarea actual, las ya vinculadas (en cualquier sentido)
 * y archivadas.
 */

// Chip de estado coloreado por categoria. Reusa CATEGORIA_COLOR del modulo.
const EstadoDot = ({ categoria }) => (
  <span
    className="w-1.5 h-1.5 rounded-full shrink-0"
    style={{ backgroundColor: CATEGORIA_COLOR[categoria] || '#94a3b8' }}
    title={CATEGORIA_LABEL[categoria] || 'Sin estado'}
  />
);

// Fila de una dependencia. Click en la fila no hace nada en v1 (futuro: abrir esa
// tarea en el modal sin perder esta).
const DepRow = ({ item, onRemove }) => {
  const hecha = item.estado_categoria === 'hecho';
  const cancelada = item.estado_categoria === 'cancelado';
  const opacidad = hecha || cancelada ? 'opacity-60' : '';
  return (
    <div className={`group flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 ${opacidad}`}>
      <EstadoDot categoria={item.estado_categoria} />
      <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 shrink-0">
        {item.codigo}
      </span>
      <span className={`flex-1 text-xs text-slate-700 truncate ${hecha ? 'line-through' : ''}`}>
        {item.titulo}
      </span>
      <span className="text-[10px] text-slate-400 shrink-0">
        {CATEGORIA_LABEL[item.estado_categoria] || ''}
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="text-slate-300 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100"
        title="Quitar dependencia"
      >
        <X size={11} />
      </button>
    </div>
  );
};

// Picker inline para agregar una dependencia. Lista tareas del proyecto
// filtrables por busqueda, excluyendo las que ya estan vinculadas.
const AddPicker = ({ tareaActualId, candidatos, excluirIds, onSelect, onCancel }) => {
  const [q, setQ] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) onCancel?.();
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [onCancel]);

  const filtrados = useMemo(() => {
    const exc = new Set(excluirIds);
    exc.add(tareaActualId);
    const query = q.trim().toLowerCase();
    return (candidatos || [])
      .filter((t) => !exc.has(t.id))
      .filter((t) =>
        !query
        || (t.titulo || '').toLowerCase().includes(query)
        || (t.codigo || '').toLowerCase().includes(query)
      )
      .slice(0, 20);
  }, [candidatos, excluirIds, tareaActualId, q]);

  return (
    <motion.div
      ref={rootRef}
      initial={{ opacity: 0, y: -2 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -2 }}
      transition={{ duration: 0.1 }}
      className="bg-white border border-slate-200 rounded-md shadow-sm"
    >
      <div className="relative border-b border-slate-100">
        <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          autoFocus
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar tarea del proyecto..."
          className="w-full pl-7 pr-2 py-1.5 text-xs bg-white placeholder-slate-400 focus:outline-none"
        />
      </div>
      <div className="max-h-40 overflow-y-auto">
        {filtrados.length === 0 ? (
          <div className="px-3 py-2 text-[11px] text-slate-400">
            {q ? 'Sin coincidencias.' : 'No hay otras tareas disponibles.'}
          </div>
        ) : (
          filtrados.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelect(t.id)}
              className="flex items-center gap-2 w-full px-2 py-1 text-left text-xs hover:bg-slate-50 transition-colors"
            >
              <EstadoDot categoria={t.estado?.categoria} />
              <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 shrink-0">
                {t.codigo}
              </span>
              <span className="flex-1 truncate text-slate-700">{t.titulo}</span>
            </button>
          ))
        )}
      </div>
    </motion.div>
  );
};

const DependenciasSection = ({ tarea, proyecto, onChanged }) => {
  const [adding, setAdding] = useState(null); // 'bloqueada_por' | 'bloquea_a' | null
  const [candidatos, setCandidatos] = useState([]);
  const [loadingCand, setLoadingCand] = useState(false);
  const [error, setError] = useState('');
  // Confirmacion de quitar dependencia: cadena de bloqueo puede ser sutil de
  // reconstruir, conviene preguntar (no es trivialmente reversible).
  const [pendingQuitar, setPendingQuitar] = useState(null); // { item, kind }

  const bloqueadaPor = useMemo(() => tarea.bloqueada_por || [], [tarea.bloqueada_por]);
  const bloqueaA = useMemo(() => tarea.bloquea_a || [], [tarea.bloquea_a]);

  // IDs vinculados en cualquier sentido (para excluir del picker y no doblar).
  const yaVinculadosIds = useMemo(
    () => [
      ...bloqueadaPor.map((d) => d.id),
      ...bloqueaA.map((d) => d.id),
    ],
    [bloqueadaPor, bloqueaA]
  );

  const cargarCandidatos = useCallback(async () => {
    if (!proyecto?.id) return;
    if (candidatos.length > 0) return;
    setLoadingCand(true);
    try {
      const { data } = await axiosInstance.get(`/api/proyectos/${proyecto.id}/tareas`);
      setCandidatos(data || []);
    } catch (err) {
      console.error('Error cargando tareas del proyecto:', err);
    } finally {
      setLoadingCand(false);
    }
  }, [proyecto?.id, candidatos.length]);

  const abrirAdd = async (kind) => {
    setError('');
    setAdding(kind);
    await cargarCandidatos();
  };

  const agregar = async (otraTareaId) => {
    setError('');
    try {
      if (adding === 'bloqueada_por') {
        // current depende de otra
        await axiosInstance.post(`/api/tareas/${tarea.id}/dependencias`, {
          depende_de_tarea_id: otraTareaId,
          tipo: 'bloquea',
        });
      } else {
        // current bloquea a otra (= otra depende de current)
        await axiosInstance.post(`/api/tareas/${otraTareaId}/dependencias`, {
          depende_de_tarea_id: tarea.id,
          tipo: 'bloquea',
        });
      }
      onChanged?.();
      setAdding(null);
    } catch (err) {
      console.error('Error agregando dependencia:', err);
      setError(err.response?.data?.error || 'No pudimos agregar la dependencia.');
    }
  };

  const confirmarQuitar = async () => {
    if (!pendingQuitar) return;
    const { item, kind } = pendingQuitar;
    setPendingQuitar(null);
    setError('');
    try {
      // dep_id pertenece a la fila de tarea_dependencia; el DELETE acepta tarea_id
      // de cualquier extremo. Usamos la tarea actual cuando es "bloqueada_por";
      // cuando es "bloquea_a" la dep "vive" en la otra tarea, asi que la URL pasa
      // por la otra; el backend valida que el dep involucre a esa tarea.
      const ruta = kind === 'bloqueada_por'
        ? `/api/tareas/${tarea.id}/dependencias/${item.dep_id}`
        : `/api/tareas/${item.id}/dependencias/${item.dep_id}`;
      await axiosInstance.delete(ruta);
      onChanged?.();
    } catch (err) {
      console.error('Error quitando dependencia:', err);
      setError('No pudimos quitar la dependencia.');
    }
  };

  // Si no hay nada en ningun lado y el usuario aun no clickeo "Agregar", renderizar
  // un block compacto que invita a vincular. Asi no ocupa espacio innecesario.
  const vacio = bloqueadaPor.length === 0 && bloqueaA.length === 0;

  return (
    <div>
      <h3 className="text-[11px] font-semibold tracking-widest uppercase text-slate-400 mb-2">
        Dependencias
      </h3>

      {error && (
        <div className="flex items-start gap-1.5 mb-2 px-2 py-1 bg-rose-50 border border-rose-200/70 rounded text-[11px] text-rose-700">
          <AlertCircle size={11} className="mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
        </div>
      )}

      {vacio && adding === null ? (
        <div className="flex items-center gap-3 px-3 py-3 bg-slate-50/60 border border-dashed border-slate-200 rounded-md text-xs text-slate-500">
          <span className="flex-1">
            Vincula tareas para marcar qué bloquea a qué dentro del proyecto.
          </span>
          <button
            type="button"
            onClick={() => abrirAdd('bloqueada_por')}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 rounded-md transition-colors"
          >
            <Plus size={11} /> Vincular
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Bloqueada por */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-500">
                Bloqueada por
                {bloqueadaPor.length > 0 && (
                  <span className="ml-1 tabular-nums text-slate-400">({bloqueadaPor.length})</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => abrirAdd('bloqueada_por')}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
              >
                <Plus size={10} /> Agregar
              </button>
            </div>
            <div className="space-y-0.5">
              {bloqueadaPor.map((item) => (
                <DepRow
                  key={item.dep_id}
                  item={item}
                  onRemove={() => setPendingQuitar({ item, kind: 'bloqueada_por' })}
                />
              ))}
              <AnimatePresence>
                {adding === 'bloqueada_por' && (
                  <AddPicker
                    tareaActualId={tarea.id}
                    candidatos={candidatos}
                    excluirIds={yaVinculadosIds}
                    onSelect={agregar}
                    onCancel={() => setAdding(null)}
                  />
                )}
              </AnimatePresence>
              {loadingCand && adding === 'bloqueada_por' && (
                <div className="px-2 py-1 text-[11px] text-slate-400">Cargando tareas...</div>
              )}
            </div>
          </div>

          {/* Bloquea a */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-500">
                Bloquea a
                {bloqueaA.length > 0 && (
                  <span className="ml-1 tabular-nums text-slate-400">({bloqueaA.length})</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => abrirAdd('bloquea_a')}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
              >
                <Plus size={10} /> Agregar
              </button>
            </div>
            <div className="space-y-0.5">
              {bloqueaA.map((item) => (
                <DepRow
                  key={item.dep_id}
                  item={item}
                  onRemove={() => setPendingQuitar({ item, kind: 'bloquea_a' })}
                />
              ))}
              <AnimatePresence>
                {adding === 'bloquea_a' && (
                  <AddPicker
                    tareaActualId={tarea.id}
                    candidatos={candidatos}
                    excluirIds={yaVinculadosIds}
                    onSelect={agregar}
                    onCancel={() => setAdding(null)}
                  />
                )}
              </AnimatePresence>
              {loadingCand && adding === 'bloquea_a' && (
                <div className="px-2 py-1 text-[11px] text-slate-400">Cargando tareas...</div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!pendingQuitar}
        title="¿Quitar esta dependencia?"
        message={pendingQuitar ? `Se desvinculará "${pendingQuitar.item.codigo} · ${pendingQuitar.item.titulo}". Puedes volver a vincularla luego.` : ''}
        variant="warning"
        confirmLabel="Quitar"
        onConfirm={confirmarQuitar}
        onClose={() => setPendingQuitar(null)}
      />
    </div>
  );
};

export default DependenciasSection;
